// stores/useDateStore.ts
import type { AppProps } from '@/types/appTypes';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import { computed, nextTick, readonly, ref } from 'vue';

import axios from 'axios';

import { syncStoresFromProps } from '@/stores/initStores';

export const useDateStore = () => {
    const currentDate = ref(''); // YYYY-MM-DD
    const dateRange = ref<string[]>([]); // array of YYYY-MM-DD

    const ZURICH_TZ = 'Europe/Zurich';
    const _updating = ref(false);

    const getNowInZurich = (): Date => {
        return toZonedTime(new Date(), ZURICH_TZ);
    };

    const dateToZurichTime = (localDate: Date): Date => {
        if (!(localDate instanceof Date)) {
            throw new Error('Invalid Date object');
        }
        return new Date(localDate.toLocaleString(undefined, { timeZone: 'Europe/Zurich' }));
    };

    const strToZurichTime = (dateString: string): Date => {
        // if (dateString === '') {
        //     return;
        // }
        // Validate YYYY-MM-DD format
        if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
            throw new Error('Expected YYYY-MM-DD format. Got: ' + dateString);
        }
        // Create date at midnight UTC (consistent across browsers)
        const utcMidnight = new Date(dateString + 'T00:00:00Z');
        return dateToZurichTime(utcMidnight);
    };

    const setToTodayAppTZ = () => {
        const todayZH = getNowInZurich();
        // currentDate.value = format(todayZH, 'yyyy-MM-dd');
        setCurrDateAppTZ(format(todayZH, 'yyyy-MM-dd'));
    };

    const setToTomorrowAppTZ = () => {
        const appDate = toZonedTime(new Date(currentDate.value), ZURICH_TZ);
        appDate.setDate(appDate.getDate() + 1);
        setCurrDateAppTZ(format(appDate, 'yyyy-MM-dd'));
    };

    const setToYesterdayAppTZ = () => {
        const appDate = toZonedTime(new Date(currentDate.value), ZURICH_TZ);
        appDate.setDate(appDate.getDate() - 1);
        setCurrDateAppTZ(format(appDate, 'yyyy-MM-dd'));
    };

    const setCurrDateAppTZ = (dateString: string) => {
        //console.log('🟢 useDateStore setCurrDateAppTZ:', dateString);
        currentDate.value = dateString;

        // --- This is where the Server update magic happens ---
        _updateDateOnServer(dateString);
    };

    /**
     * Is the App's Date equal to Today in Zurich Timezone?
     * @returns boolean
     */
    const isToday = computed((): boolean => {
        const todayZH = getNowInZurich();
        const todayStr = format(todayZH, 'yyyy-MM-dd');
        return currentDate.value === todayStr;
    });

    /**
     * Is the given date string (YYYY-MM-DD) today in Zurich Timezone?
     * @param dateString - Date in YYYY-MM-DD format
     * @returns boolean
     */
    const isDateToday = (dateString: string): boolean => {
        if (!dateString || dateString.length !== 10) return false;
        const todayZH = getNowInZurich();
        const todayStr = format(todayZH, 'yyyy-MM-dd');
        return dateString === todayStr;
    };

    /**
     * Format a date string (YYYY-MM-DD) into a more readable format.
     * Example: "2024-06-15" -> "June 15th, 2024"
     * @param dateString
     * @returns
     */
    const formatViewDate = (dateString: string, monthLength: 'short' | 'long' = 'short'): string => {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];

        if (monthLength === 'long') {
            monthNames[0] = 'January';
            monthNames[1] = 'February';
            monthNames[2] = 'March';
            monthNames[3] = 'April';
            monthNames[4] = 'May';
            monthNames[5] = 'June';
            monthNames[6] = 'July';
            monthNames[8] = 'September';
            monthNames[9] = 'October';
            monthNames[10] = 'November';
            monthNames[11] = 'December';
        }

        if (!dateString) return ''; // ✅ Safe default

        const [year, month, dayStr] = dateString.split('-');
        const day = parseInt(dayStr, 10);

        const getDaySuffix = (d: number): string => {
            if (d > 3 && d < 21) return 'th';
            switch (d % 10) {
                case 1:
                    return 'st';
                case 2:
                    return 'nd';
                case 3:
                    return 'rd';
                default:
                    return 'th';
            }
        };

        return `${monthNames[parseInt(month, 10) - 1]} ${day}${getDaySuffix(day)}, ${year}`;
    };

    /**
     * Working!!!
     *
     * This gets called from initStores syncStoresFromProps()
     * In theory it should always get the latest data required for this store
     * whenever the usePage().props changes on navigation.
     * @param props
     */
    const _updateFromProps = (props: AppProps) => {
        // if (props.currDateZH_YYYYMMDD) {
        // console.log('🟢 useDateStore updateFromProps:', props.currDateZH_YYYYMMDD);
        currentDate.value = props.currDateZH_YYYYMMDD;
        // console.log('🟢 useDateStore updateFromProps:', props.dateRange);
        dateRange.value = props.dateRange || [];
        // }
    };

    return {
        currentDate,
        dateRange,
        isToday, // Boolean -> AppDate === today in Zurich TZ
        isDateToday, // Takes a date string and tells if it's today in Zurich TZ
        updating: readonly(_updating), /// is the store currently updating
        formatViewDate,
        setToTodayAppTZ,
        setToTomorrowAppTZ,
        setToYesterdayAppTZ,
        setCurrDateAppTZ,
        getNowInZurich,
        dateToZurichTime,
        strToZurichTime,
        // Helper date stuff.
        getWeekdaysName,
        // Used to sync store from Inertia props on page load/navigation
        updateFromProps: _updateFromProps,
    };

    /**
     * The *Only* place in the app to set the Current Date.
     *
     * newDate: string YYYY-MM-DD format. Always in Zurich Timezone.
     */
    async function _updateDateOnServer(newZHDateYYYYMMDD: string) {
        const myDateRange = dateRange.value || undefined;
        if (myDateRange) {
            const startDate = myDateRange[1]; // 2nd date is the WeekCal Sunday start
            const endDate = myDateRange[myDateRange.length - 2]; // 2nd last date is the WeekCal Saturday end

            // Normalize dates to ignore time component for comparison
            const normAppDate = new Date(newZHDateYYYYMMDD + 'T00:00:00');
            const normStartDate = new Date(startDate + 'T00:00:00');
            const normEndDate = new Date(endDate + 'T00:00:00');

            if (normAppDate >= normStartDate && normAppDate <= normEndDate) {
                // Within range
                axios.patch(route('dashboard'), {
                    currDateZH_YYYYMMDD: newZHDateYYYYMMDD,
                });

                currentDate.value = newZHDateYYYYMMDD;
                return;
            }
        }

        // The UI can hook into this to show a spinner (e.g. WeekCal)
        _onLoadStart();

        try {
            // 1. Update server state
            await axios.patch(route('dashboard'), {
                currDateZH_YYYYMMDD: newZHDateYYYYMMDD,
            });

            // Fetch only WeekCal data from server
            const response = await axios.get(route('dashboard'), {
                params: {
                    only: 'currDateZH_YYYYMMDD,maxSlots,currentSchedule,dateRange,bookingsRange,activeSchedules_overrides,activeSchedules_base,blockedSlots',
                },
            });

            // // Merge new props (make sure to preserve reactivity)
            // Object.assign(page.props, response.data.props);      // this was old way. Can remove at some point.

            // Re-sync all stores from updated props.
            // NOTE: We're not reloading the actual page, so usePage().props doesn't auto-update.
            syncStoresFromProps(response.data.props as AppProps);

            // console.log('WeekCal props refreshed via Axios → full data update, no scroll loss');
        } catch (error) {
            console.error('Dashboard refresh failed in Date Store:', error);
        } finally {
            _onLoadFinish();
        }
    }

    // event that the UI can hook into (show spinner)
    function _onLoadStart() {
        _updating.value = true;
    }
    function _onLoadFinish() {
        nextTick(() => {
            _updating.value = false;
        });
    }

    /**
     *
     * ================================================
     * Helper Date Functions
     * ================================================
     *
     */

    /**
     * Takes a YYYY-MM-DD string and returns the long day name
     * (e.g., 'Monday') in Zurich timezone.
     * @param inputStringYYYYMMDD - Date string like '2025-12-05'
     * @returns string 'Monday'
     */
    function getWeekdaysName(inputStringYYYYMMDD: string, format: 'long' | 'short' = 'long'): string {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const daysShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const zhrDate = toZonedTime(inputStringYYYYMMDD + 'T00:00:00', ZURICH_TZ);
        const dayIndex = zhrDate.getDay();
        return format === 'short' ? daysShort[dayIndex] : days[dayIndex];
    }
};
